import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ladeAlles,
  selectNotizen,
  selectOrdner,
  selectDateien,
  selectWissenStatus,
} from 'modules/wissen/features/wissenSlice';
import {
  pruefeTresorStatus,
  sperren,
  selectTresorStatus,
} from 'core/features/sicherheit/sicherheitSlice';
import { projectVisited } from 'core/features/projects/projectsSlice';
import BackToOverview from 'core/components/BackToOverview';
import PageBackground from 'core/components/PageBackground';
import GesichertHinweis from 'core/features/sicherheit/GesichertHinweis';
import WissenBrowser from 'modules/wissen/components/WissenBrowser';
import NotizEditor from 'modules/wissen/components/NotizEditor';

/**
 * Einstieg des Wissen-Moduls. Der Wissensbereich ist nur im gesicherten Modus
 * erreichbar (Tresor wird auf der Uebersicht entsperrt); hier werden im
 * entsperrten Zustand Ordner, Notizen und Dateien in einer karten-basierten
 * Uebersicht gezeigt.
 */
export default function WissenPage() {
  const dispatch = useDispatch();
  const tresorStatus = useSelector(selectTresorStatus);

  // Ende-zu-Ende-Verschluesselung braucht die WebCrypto API, die nur im Secure
  // Context (HTTPS oder localhost) verfuegbar ist.
  const kryptoVerfuegbar = typeof window !== 'undefined' && window.crypto && window.crypto.subtle;

  useEffect(() => {
    if (!kryptoVerfuegbar) return;
    dispatch(projectVisited('wissen'));
    dispatch(pruefeTresorStatus());
  }, [dispatch, kryptoVerfuegbar]);

  // Im gesicherten Modus (entsperrter Tresor) Inhalte laden.
  useEffect(() => {
    if (tresorStatus === 'unlocked') dispatch(ladeAlles());
  }, [tresorStatus, dispatch]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <PageBackground name="wissen" />
      <div className="mb-6">
        <BackToOverview />
      </div>

      {!kryptoVerfuegbar && <KryptoHinweis />}

      {kryptoVerfuegbar && tresorStatus === 'unknown' && <p className="text-center text-ink-muted">Lade…</p>}
      {kryptoVerfuegbar && tresorStatus !== 'unknown' && tresorStatus !== 'unlocked' && (
        <GesichertHinweis bereich="Der Wissensbereich" />
      )}
      {kryptoVerfuegbar && tresorStatus === 'unlocked' && <Tresor />}
    </div>
  );
}

/** Hinweis, wenn die WebCrypto API fehlt (unverschluesselter HTTP-Zugriff). */
function KryptoHinweis() {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-line bg-paper-soft p-6 text-center">
      <div className="mb-2 text-3xl">🔓</div>
      <h1 className="font-serif text-xl font-semibold text-ink">Verschlüsselung nicht verfügbar</h1>
      <p className="mt-2 text-sm text-ink-muted">
        Dieser Bereich verschlüsselt alles direkt im Browser und benötigt dafür eine sichere Verbindung
        (HTTPS) oder den Zugriff über <code className="rounded bg-paper-sunken px-1">localhost</code>.
        Rufe die Seite über HTTPS auf, um deinen Tresor zu nutzen.
      </p>
    </div>
  );
}

/** Entsperrter Bereich: Portal-artiger Kopf, Karten-Browser und Vollbild-Editor. */
function Tresor() {
  const dispatch = useDispatch();
  const notizen = useSelector(selectNotizen);
  const ordner = useSelector(selectOrdner);
  const dateien = useSelector(selectDateien);
  const status = useSelector(selectWissenStatus);

  const [editorNotiz, setEditorNotiz] = useState(null);

  const neueNotiz = (ordnerId, favorit) =>
    setEditorNotiz({ ordnerId: ordnerId ?? null, favorit: !!favorit, titel: '', text: '', tags: [] });

  return (
    <>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1 font-mono text-xs uppercase tracking-[0.2em] text-ink-muted">Wissensbasis</p>
          <h1 className="font-serif text-4xl font-semibold tracking-tight text-ink">🧠 Wissen</h1>
          <p className="mt-2 text-sm text-ink-soft">Ende-zu-Ende verschlüsselt · nur du kannst die Inhalte lesen.</p>
        </div>
        <button
          type="button"
          onClick={() => dispatch(sperren())}
          className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
        >
          🔒 Sperren
        </button>
      </header>

      {status === 'loading' && <p className="mb-4 text-ink-muted">Lade und entschlüssle…</p>}

      <WissenBrowser
        notizen={notizen}
        ordner={ordner}
        dateien={dateien}
        onOeffnenNotiz={setEditorNotiz}
        onNeueNotiz={neueNotiz}
      />

      {editorNotiz && (
        <NotizEditor notiz={editorNotiz} ordner={ordner} dateien={dateien} onClose={() => setEditorNotiz(null)} />
      )}
    </>
  );
}
